import { useEffect, useState } from 'react';
import { toast } from '../../../util/toasts';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { Order, SeisCertificate } from '../../../clients/quipli-service/order/order';
import { DateTime } from 'luxon';
import { displayMoneyFormat } from '../../../util/util';
import { Collapsible } from '../../../components/collapsible/collapsible';
import Icon from '../../../components/icons/icon';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import classNames from 'classnames';
import { capitalize } from 'lodash';
import { Button } from '../../../components/form/button';
import { OrderSection } from '../order-section/order-section';
import { Location } from '../../../clients/quipli-service/location/location';

export const SeisDamageWaiversModal = ({ order, location }: { order: Order; location: Location }) => {
  const { getSeisDamageWaiversByOrderId } = useOrderApiV1();
  const [certs, setCerts] = useState<SeisCertificate[][]>();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const init = async () => {};
    init();
  }, []);

  useEffect(() => {
    const init = async () => {
      if (isOpen) {
        const _certs = await getSeisDamageWaiversByOrderId(order.id).catch((e) => {
          console.error(e);
          toast('Failed to load Rental Damage Waiver Insurance', 'error');
          return undefined;
        });
        setCerts(
          Object.values(_certs ?? {}).sort(
            (a, b) =>
              (a.find((c) => c.status.includes('approved'))?.start_date ?? 0) -
              (b.find((c) => c.status.includes('approved'))?.start_date ?? 0),
          ),
        );
      }
    };

    init();
  }, [isOpen]);

  return (
    <OrderSection>
      <div className="flex justify-between items-center">
        <Typography type="text-md" weight="medium" className="text-grey-800">
          Rental Damage Waiver Insurance
        </Typography>
        <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" onClick={() => setIsOpen(!isOpen)} />
      </div>
      {isOpen ? (
        <>
          {!certs ? (
            <Spinner />
          ) : certs && Object.keys(certs).length ? (
            <div>
              <div className="grid grid-cols-5 gap-2">
                <div>
                  <p className="text-left text-gray-500">Product</p>
                </div>
                <div>
                  <p className="text-left text-gray-500">Serial No.</p>
                </div>
                <div>
                  <p className="text-left text-gray-500">Certificate No.</p>
                </div>
                <div>
                  <p className="text-left text-gray-500">Status</p>
                </div>
                <Button
                  className="self-center p-[6px] h-min button button-secondary"
                  onClick={() =>
                    window.open('https://quipli-service-prod-public.s3.us-east-1.amazonaws.com/rdw_claim_form.pdf')
                  }
                >
                  <span className="flex gap-2 items-center">
                    <Icon icon="Download" />
                    <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] ">
                      Claim Form{' '}
                    </Typography>
                  </span>
                </Button>
              </div>
              {Object.values(certs).map((certsOfCertNumber) => {
                const currentCert =
                  certsOfCertNumber.find((c) => c.status.toLowerCase().match(/approved.*current/)) ??
                  certsOfCertNumber[certsOfCertNumber.length - 1];
                const updateRequestedCert = certsOfCertNumber.find((c) => c.status.includes('update requested'));

                return (
                  <div className="col-span-4">
                    <Collapsible
                      trigger={(isOpen) => {
                        const product = order.products.find((p) =>
                          p.assignedUnits.find((u) => u.id === currentCert.product_unit_id),
                        );
                        return (
                          <div className="grid grid-cols-5">
                            <p className="text-left w-full">{product?.product.name}</p>
                            <p className="text-left w-full">
                              {currentCert.manufacturer_serial_number ||
                                product?.assignedUnits.find((u) => u.id === currentCert.product_unit_id)
                                  ?.serialNumber ||
                                '-'}
                            </p>
                            <p className="text-left w-full">{currentCert.certificate_number}</p>
                            <p
                              className={classNames('text-left w-full p-1 rounded-md w-max', {
                                'bg-green-300': currentCert.status.includes('approved') && !updateRequestedCert,
                                'text-green-700': currentCert.status.includes('approved') && !updateRequestedCert,
                                'bg-yellow-300':
                                  updateRequestedCert && updateRequestedCert.status.includes('update requested'),
                                'text-yellow-700':
                                  updateRequestedCert && updateRequestedCert.status.includes('update requested'),
                              })}
                            >
                              {updateRequestedCert
                                ? capitalize(updateRequestedCert.status)
                                : capitalize(currentCert.status)}
                            </p>
                            <p className="text-right w-full">
                              {' '}
                              <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
                            </p>
                          </div>
                        );
                      }}
                    >
                      <div className="p-3 bg-gray-100 rounded-md">
                        <Typography type="text-md" weight="semi-bold">
                          Current Approved RDW
                        </Typography>
                        <div className="gap-4 grid grid-cols-3">
                          <div>
                            <Typography type="text-sm" className="text-gray-500">
                              Start Date
                            </Typography>
                            <Typography type="text-md">
                              {DateTime.fromMillis(currentCert.start_date).setZone(location.timezone).toFormat('ff')}
                            </Typography>
                          </div>
                          <div>
                            <Typography type="text-sm" className="text-gray-500">
                              End Date
                            </Typography>
                            <Typography type="text-md">
                              {DateTime.fromMillis(currentCert.end_date).setZone(location.timezone).toFormat('ff')}
                            </Typography>
                          </div>
                          <div>
                            <Typography type="text-sm" className="text-gray-500">
                              Rental Amount
                            </Typography>
                            <Typography type="text-md">
                              {displayMoneyFormat(currentCert.retail_rental_amount, location.currency)}
                            </Typography>
                          </div>
                          <div>
                            <Typography type="text-sm" className="text-gray-500">
                              Deductible
                            </Typography>
                            <Typography type="text-md">
                              {displayMoneyFormat(currentCert.deductible, location.currency)}
                            </Typography>
                          </div>
                          <div>
                            <Typography type="text-sm" className="text-gray-500">
                              Certificate Rate
                            </Typography>
                            <Typography type="text-md">{currentCert.certificate_rate * 100}%</Typography>
                          </div>
                          <div>
                            <Typography type="text-sm" className="text-gray-500">
                              Certificate Cost
                            </Typography>
                            <Typography type="text-md">
                              {displayMoneyFormat(
                                +(currentCert.certificate_rate * currentCert.retail_rental_amount).toFixed(2),
                                location.currency,
                              )}
                            </Typography>
                          </div>
                        </div>
                        {updateRequestedCert && (
                          <div className="mt-4">
                            <Typography className="text-yellow-500" type="text-md" weight="semi-bold">
                              Update Requested:
                            </Typography>
                            <div className="text-yellow-600 rounded-md gap-4 grid grid-cols-3">
                              <div>
                                <Typography type="text-xs" className="text-gray-500">
                                  Start Date
                                </Typography>
                                <Typography type="text-sm" className="text-yellow-500">
                                  {DateTime.fromMillis(updateRequestedCert.start_date)
                                    .setZone(location.timezone)
                                    .toFormat('ff')}
                                </Typography>
                              </div>
                              <div>
                                <Typography type="text-xs" className="text-gray-500">
                                  End Date
                                </Typography>
                                <Typography type="text-sm" className="text-yellow-500">
                                  {DateTime.fromMillis(updateRequestedCert.end_date)
                                    .setZone(location.timezone)
                                    .toFormat('ff')}
                                </Typography>
                              </div>
                              <div>
                                <Typography type="text-xs" className="text-gray-500">
                                  Rental Amount
                                </Typography>
                                <Typography className="text-yellow-500" type="text-sm">
                                  {displayMoneyFormat(updateRequestedCert.retail_rental_amount, location.currency)}
                                </Typography>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center">
              <p>No rental damage waiver insurance found for this order.</p>
            </div>
          )}
        </>
      ) : (
        <></>
      )}
    </OrderSection>
  );
};
